import type { ReactNode } from "react";
import { devMode } from "../auth/auth";
import { Icon, Orb, Pill, useStudio } from "../components/ui";
import { TodayCal, TodayMail, TodayTeams } from "../components/Today";
import { PROJECTS } from "../data/sample";
import { useGraph, type CalEvent, type Mail, type Task } from "../lib/m365";
import { todayPicks } from "../lib/today";
import { longDate, t, T } from "../lib/i18n";
import { mailFromGoogle } from "../lib/m365";
import { TodayDeadlines } from "../components/Today";
import { GreetingBar } from "../components/GreetingBar";
import { useHomePrefs } from "../lib/homePrefs";

/** Opens Workspace in the conversation the person already has, asking about what Home shows: same VERA, same knowledge. */
export function AskAboutHome() {
  const { go } = useStudio();
  return <button className="btn" onClick={() => go("workspace", { ask: T("Look at my Home page with me: what needs me today, what's open and what changed?"), title: T("My Home page") })}><Icon name="vera" size={15} /> {T("Ask VERA about this page")}</button>;
}

export function Home({ afterGreeting }: { afterGreeting?: ReactNode } = {}) {
  const { user, person, profile, go, m365, lang } = useStudio();
  const first = person?.first ?? user.name.split(" ")[0];
  const hour = new Date().getHours();
  const greet = t(lang, hour < 12 ? "home.morning" : hour < 18 ? "home.afternoon" : "home.evening");
  // Live: worked out from this person's own Planner, Outlook and calendar. The demo keeps its sample people.
  const outlook = m365.mail(), planner = m365.connected("planner");
  const tasks = useGraph<Task[]>("planner.tasks", !devMode && planner);
  const events = useGraph<CalEvent[]>("calendar.day", !devMode && outlook, { days: 1 });
  const mail = useGraph<Mail[]>("mail.inbox", !devMode && outlook);
  const live = todayPicks({ tasks: planner ? tasks.data : null, events: outlook ? events.data : null, mail: outlook ? mail.data : null });
  const priorities = devMode ? (person?.priorities ?? []).map((p) => ({ text: p, why: "", ask: p })) : live.priorities;
  const noticed = devMode ? person?.noticed ?? [] : live.noticed;
  const loadingToday = !devMode && (tasks.loading || events.loading || mail.loading) && !priorities.length;
  const google = mailFromGoogle();
  const source = [planner && "Planner", outlook && (google ? "Gmail, Google Calendar" : t(lang, "src.outlook"))].filter(Boolean).join(", ");
  const { shows } = useHomePrefs();
  const teamsCard = google || profile.areas.includes("proposals") && !m365.connected("teams");
  return (
    <>
      <section className="hello">
        <Orb mode="breathe" size={74} />
        <div className="stack" style={{ gap: 6, flex: 1, minWidth: 0 }}>
          <span className="label">{longDate(lang)}</span>
          <h1>{greet}, {first}.</h1>
          <p className="mute">{t(lang, "home.lead")}</p>
          <div><AskAboutHome /></div>
        </div>
        <GreetingBar />
      </section>
      {afterGreeting}

      {(shows("three") || shows("noticed")) && <div className={shows("three") && shows("noticed") ? "split" : "stack"}>
        {shows("three") && <div className="card">
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}><h2>{t(lang, "home.three")}</h2><span className="faint">{devMode ? (lang === "sq" ? "Nga lista e punëve, emaili dhe VERA" : T("From the tracker, email and VERA")) : source ? t(lang, "home.from", { source }) : ""}</span></div>
          {priorities.length ? (
            <div className="list">{priorities.map((p, i) => (
              <div className="li" key={p.text}><span className="ico num" style={{ fontWeight: 700 }}>{i + 1}</span><div className="grow">{p.text}{p.why && <div className="faint" style={{ fontSize: 12 }}>{p.why}</div>}</div>
                <button className="btn" onClick={() => go("workspace", { ask: p.ask, fresh: true, title: p.text.slice(0, 60) })}>{t(lang, "home.workon")}</button></div>))}
            </div>
          ) : loadingToday ? <p className="faint">{t(lang, "home.looking")}</p>
            : !outlook && !planner ? <p className="faint">{t(lang, "home.connectAny")}</p>
            : <p className="faint">{t(lang, "home.nothing")}</p>}
        </div>}
        {shows("noticed") && <div className="card">
          <div className="row" style={{ gap: 8, marginBottom: 10 }}><Orb size={16} /><h2>{t(lang, "home.noticed")}</h2></div>
          <div className="noticed">{noticed.length ? noticed.map((n) => <div className="notice calm" key={n}><span className="mute">{n}</span></div>)
            : <p className="faint">{loadingToday ? t(lang, "home.looking") : t(lang, "home.noticedAny")}</p>}</div>
        </div>}
      </div>}

      {(shows("calendar") || shows("inbox") || shows("teams")) && <div className="grid g3">
        {shows("calendar") && <div className="card stack"><div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 18 }}>{t(lang, "home.calendar")}</h2><span className="src"><Icon name="cal" size={13} /> {google ? T("Google Calendar") : m365.mail() ? "Outlook" : ""}</span></div><TodayCal /></div>}
        {shows("inbox") && <div className="card stack"><div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 18 }}>{t(lang, "home.inbox")}</h2><span className="src"><Icon name="mail" size={13} /> {google ? "Gmail" : m365.mail() ? "Outlook" : ""}</span></div><TodayMail /></div>}
        {/* Google users and companies with proposals see their proposal deadlines; Microsoft users see Teams. */}
        {!shows("teams") ? null : teamsCard
          ? <div className="card stack"><div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 18 }}>{t(lang, "today.deadlines")}</h2><span className="src"><Icon name="doc" size={13} /> VERA</span></div><TodayDeadlines /></div>
          : <div className="card stack"><div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 18 }}>Teams</h2><span className="src"><Icon name="chat" size={13} />{" "}{T("Microsoft Teams")}</span></div><TodayTeams /></div>}
      </div>}

      {/* The sample platforms, until a team works in VERANeural HQ: its real platforms and Brain show below on Home. */}
      {profile.areas.includes("projects") && !profile.crm && (
        <div className="card">
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 12 }}><h2>{T("Platforms at a glance")}{" "}{!devMode && <span className="sampletag">{T("Sample")}</span>}</h2><button className="btn" onClick={() => go("projects")}>{T("All projects")}</button></div>
          <div className="grid g4">{PROJECTS.slice(0, 4).map((p) => (
            <button className="card" style={{ textAlign: "left", background: "var(--panel)" }} key={p.id} onClick={() => go("projects", { project: p.id })}>
              <b>{p.name}</b><div style={{ margin: "8px 0" }}><Pill tone={p.status[0]}>{p.status[1]}</Pill></div>
              <p className="faint" style={{ fontSize: 12.5 }}>{T("Next:")}{" "}{p.next}</p>
            </button>))}
          </div>
        </div>
      )}
    </>
  );
}
